import { useEffect, useState } from "react";

import {
  ListContainer,
  ListContext,
  Page,
  PageRoute,
  SearchBar,
  Stack,
  buildPath,
  buildTemplatePath,
  pn
} from "@compass/core";

import { ListFooter } from "components";
import {
  Permission,
  StaffServiceTypeListDto,
  StaffServiceTypeListFilter,
  StaffServiceTypeListResource
} from "generated/openApi";
import { useInitListFilter, useList, useListFilter } from "hooks";
import produce from "immer";
import { Outlet } from "react-router-dom";
import ApiService from "services/apiService";
import { StaffServiceTypeFilter } from "../StaffServiceTypeFilter";
import { StaffServiceTypeListPageHeader } from "./StaffServiceTypeListPageHeader";
import { StaffServiceTypeTable } from "./StaffServiceTypeTable";

const routeName = "staffServiceTypes";

export const staffServiceTypeListPageRoute: PageRoute = {
  handle: "Staff Service Types",
  templatePath: buildTemplatePath(routeName),
  getPath: () => buildPath(routeName),
};

export const StaffServiceTypeListPage = () => {
  const ROOT_PERMISSION: Permission = Permission.MatchUtility;

  const [listItems, setListItems] = useState<StaffServiceTypeListDto[]>([]);
  const { listFilter, setListFilter } = useInitListFilter<StaffServiceTypeListFilter>({
    sortObjects: [{ property: pn<StaffServiceTypeListDto>("name"), isDesc: false }],
  });
  const [initListFilter] = useState<StaffServiceTypeListFilter>(listFilter);
  const [total, setTotal] = useState<number>();
  const [resource, setResource] = useState<StaffServiceTypeListResource>({});
  const [searchFocused, setSearchFocused] = useState<boolean>(false);

  const { tableContainerRef, applyPage, applyPageSize, loadList, applySort, applySearch, applySortMethod } = useList({
    listFilter: listFilter,
    setListFilter: setListFilter,
    fetchList: (listFilter) => {
      ApiService.staffServiceTypes.getList(listFilter).then((response) => {
        setListItems(response);
      });
    },
    fetchTotal: (listFilter) => {
      setTotal(undefined);
      ApiService.staffServiceTypes.getListTotal(listFilter).then((response) => {
        setTotal(response);
      });
    },
  });

  useEffect(() => {
    loadList(listFilter);
    ApiService.staffServiceTypes.getListResource().then((response) => {
      setResource(response);
    });
  }, []);

  const listFilterNode = useListFilter<StaffServiceTypeListFilter>({
    listFilter: listFilter,
    initListFilter: initListFilter,
    onApply: (listFilter) => loadList(listFilter, { resetPage: true }),
    inputNode: (listFilter, setListFilter) => (
      <StaffServiceTypeFilter
        filter={listFilter.serviceTypeFilter || {}}
        setFilter={(filter) =>
          setListFilter(
            produce(listFilter, (draft) => {
              draft.serviceTypeFilter = filter;
            })
          )
        }
      />
    ),
  });

  return (
    <>
      <Page
        header={
          <StaffServiceTypeListPageHeader
            rootPermission={ROOT_PERMISSION}
            handle={staffServiceTypeListPageRoute.handle}
          />
        }
      >
        <ListContainer
          fullHeight
          header={
            <Stack direction="row" justifyContent="space-between">
              <SearchBar onStoppedTyping={applySearch} setSearchFocused={setSearchFocused} size="small" />
              {listFilterNode}
            </Stack>
          }
          tableContainer={
            <StaffServiceTypeTable
              rootPermission={ROOT_PERMISSION}
              tableContainerRef={tableContainerRef}
              staffServiceTypes={listItems}
              setStaffServiceTypes={setListItems}
              resource={resource}
              searchFocused={searchFocused}
              sortObjects={listFilter.sortObjects}
              sort={applySort}
              loadList={() => loadList(listFilter)}
            />
          }
          footer={
            <ListFooter
              total={total}
              size={listFilter.pageSize}
              page={listFilter.page}
              onPageChange={applyPage}
              onSizeChange={applyPageSize}
              onSortMethodChange={applySortMethod}
            />
          }
        />
      </Page>

      <ListContext.Provider value={{ loadList: (options) => loadList(listFilter, { resetPage: options?.resetPage }) }}>
        <Outlet />
      </ListContext.Provider>
    </>
  );
};
